import { useState, useEffect, useRef } from 'react';
import { X, Maximize2, Bot, Send, Loader2, AlertTriangle } from 'lucide-react';
import { useFloatingChat } from './FloatingChatContext';
import { chatService } from '@/lib/chatService';
import { useI18n } from '@/i18n/i18n';
import { ChatMessage } from './chat/ChatMessage';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { Alert, AlertDescription } from './ui/alert';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCurrentLanguage } from './LanguageRedirect';

/**
 * Floating chat window that stays open across pages until minimized
 */
const FloatingChatWindow = () => {
  const { isOpen, isMinimized, messages, setIsOpen, setIsMinimized, setMessages, setHasUnreadMessages } = useFloatingChat();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const currentLang = useCurrentLanguage();
  
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const lastMessageCountRef = useRef(messages.length);

  // Don't show window on the full chat page
  const isChatPage = location.pathname.includes('/chat');

  // Mark messages as read when window is opened
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setHasUnreadMessages(false);
    }
  }, [isOpen, isMinimized, setHasUnreadMessages]);

  // Detect new assistant messages and show notification badge if window is closed
  useEffect(() => {
    // Only trigger if messages array actually increased (new message added)
    if (messages.length > lastMessageCountRef.current && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      // If the last message is from assistant and window is closed/minimized, show badge
      if (lastMessage.role === 'assistant' && (!isOpen || isMinimized)) {
        setHasUnreadMessages(true);
      }
    }
    // Update the ref to current message count
    lastMessageCountRef.current = messages.length;
  }, [messages, isOpen, isMinimized, setHasUnreadMessages]);

  const scrollToBottom = () => {
    if (scrollAreaRef.current) {
      const scrollElement = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollElement) {
        scrollElement.scrollTo({
          top: scrollElement.scrollHeight,
          behavior: 'smooth'
        });
      }
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage || isLoading) return;

    setError('');
    setIsLoading(true);

    // Add user message immediately
    const userMessage = chatService.createUserMessage(trimmedMessage);
    setMessages(prev => [...prev, userMessage]);
    setMessage('');

    // Create a reference for assistant message ID
    const assistantMessageId = chatService.generateMessageId();
    let messageAdded = false;

    try {
      // Call the chat service with streaming callback and conversation history
      await chatService.sendMessage(trimmedMessage, {}, (chunk: string) => {
        // Only add the assistant message when we receive the first chunk (non-empty content)
        if (!messageAdded && chunk.trim()) {
          const assistantMessage = {
            id: assistantMessageId,
            role: 'assistant' as const,
            content: chunk,
            timestamp: new Date(),
          };
          setMessages(prev => [...prev, assistantMessage]);
          messageAdded = true;
        } else if (messageAdded) {
          // Update the assistant message content progressively
          setMessages(prev => 
            prev.map(msg => 
              msg.id === assistantMessageId 
                ? { ...msg, content: chunk }
                : msg
            )
          );
        }
      }, messages);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t("chat.errors.unknown");
      setError(errorMessage);
      console.error("Chat error:", err);
      
      // Remove the assistant message on error if it was added
      if (messageAdded) {
        setMessages(prev => prev.filter(msg => msg.id !== assistantMessageId));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleMaximize = () => {
    // Navigate to full chat page with conversation preserved
    navigate(`/${currentLang}/chat`);
    setIsMinimized(true); // Minimize instead of closing to keep the chat state
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsMinimized(false);
  };

  // Don't render if not open or if minimized or if on chat page
  if (!isOpen || isMinimized || isChatPage) {
    return null;
  }

  return (
    <div
      className="
        fixed bottom-6 right-6 md:bottom-8 md:right-8
        w-[90vw] max-w-[400px]
        h-[500px] md:h-[600px]
        bg-white
        rounded-2xl
        shadow-2xl
        z-[9999]
        flex flex-col
        border border-gray-200
        overflow-hidden
      "
      style={{
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <Bot className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Finn</h3>
            <p className="text-xs text-gray-600">{t("chat.container.expertTitle")}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleMaximize}
            className="p-2 hover:bg-white/80 rounded-lg transition-colors"
            title="Maximizar chat"
          >
            <Maximize2 className="w-4 h-4 text-gray-600" />
          </button>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
            title="Cerrar"
          >
            <X className="w-4 h-4 text-gray-600 hover:text-red-600" />
          </button>
        </div>
      </div>

      {/* Messages Area */}
      <ScrollArea ref={scrollAreaRef} className="flex-1 p-4">
        <div className="space-y-4">
          {messages.length === 0 && (
            <div className="text-center py-8">
              <Bot className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h4 className="text-sm font-medium text-gray-900 mb-1">
                {t("chat.container.welcome")}
              </h4>
              <p className="text-xs text-gray-600">
                {t("chat.container.welcomeMessage")}
              </p>
            </div>
          )}
          
          {messages.map((msg) => (
            <ChatMessage key={msg.id} message={msg} />
          ))}
          
          {error && (
            <Alert variant="destructive" className="text-sm">
              <AlertTriangle className="h-3 w-3" />
              <AlertDescription className="text-xs">{error}</AlertDescription>
            </Alert>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div className="flex gap-2 p-4 bg-white border-t border-gray-200 flex-shrink-0">
        <div className="flex-1 relative">
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("chat.input.placeholder")}
            disabled={isLoading}
            className="min-h-[48px] max-h-[100px] resize-none text-sm"
            rows={1}
          />
        </div>
        <Button
          onClick={handleSendMessage}
          disabled={!message.trim() || isLoading}
          size="icon"
          className="h-12 w-12 flex-shrink-0"
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
        </Button>
      </div>
    </div>
  );
};

export default FloatingChatWindow;
